import { effect, inject, Injectable, signal } from '@angular/core';
import { MultiRegistrationConfig, TicketOption, UIParticipant, UIState } from '@azavista/participants/dist/event-app/multiregistration';
import { TRANSLATION_MAP } from 'src/app/enums';
import { IField } from '../interfaces';
import { DraftSubParticipantRecord, MULTI_REGISTRATION_ATTRIBUTE_NAMES } from './types';
import { SharedService } from '../shared.service';
import { TranslateService } from '@ngx-translate/core';
import { getAssignMainParticipantField, getFieldEditableByStatus } from './utils';
import { InputFieldCreatorService } from '../input-field-creator.service';
import { DomSanitizer } from '@angular/platform-browser';
import { AttributeName as GlobalAttributeName } from 'src/app/interfaces';
import { Product } from '@azavista/payments';
import { getMapByGetter } from '../utils.service';
import { FieldComponentType } from '../enums';
import { MultiRegistrationService } from './multi-registration.service';
import { AppLoggerService, LogLevel } from '../app-logger.service';

@Injectable({
    providedIn: 'root',
})
export class MultiRegistrationFormService {
    private logSvc = inject(AppLoggerService);
    private multiRegistrationSvc = inject(MultiRegistrationService);
    readonly attributeName = MULTI_REGISTRATION_ATTRIBUTE_NAMES;

    readonly editedSubParticipantRecord = signal<DraftSubParticipantRecord>({});

    get currentLanguage() {
        return this.translateSvc.currentLang;
    }

    constructor(
        private inputFieldCreatorSvc: InputFieldCreatorService,
        private sharedSvc: SharedService,
        private translateSvc: TranslateService,
        private sanitizer: DomSanitizer,
    ) {}

    patchEditedSubParticipant = (
        subParticipantId: string,
        patch: Partial<UIParticipant>,
    ) => {
        this.editedSubParticipantRecord.update((record) => {
            const editedSubParticipant = record[subParticipantId];
            if (patch.participant_attributes) {
                patch = {
                    ...patch,
                    participant_attributes: {
                        ...(editedSubParticipant?.participant_attributes || {}),
                        ...patch.participant_attributes,
                    }
                };
            }
            Object.assign(editedSubParticipant, patch);
            this.logSvc.log(LogLevel.log, 'Multi Registration - Form - PATCH', {
                editedSubParticipantRecord: record,
                subParticipantId,
                patch,
            });
            return record;
        });
    };

    putEditedSubParticipant = (
        subParticipantId: string,
        update: UIParticipant | null | undefined,
    ) => {
        this.editedSubParticipantRecord.update((record) => {
            record[subParticipantId] = update;
            this.logSvc.log(LogLevel.log, 'Multi Registration - Form - PUT', {
                editedSubParticipantRecord: record,
                subParticipantId,
                update,
            });
            return record;
        });
    };

    getHasEditedDraft() {
        return Object.values(this.editedSubParticipantRecord()).some(recordItem => !!recordItem);
    }

    createTicketField = (
        participant: Pick<UIParticipant, 'status' | 'ticket_options' | 'ticket_type_id' | 'editable'>,
        products: Product[],
        alwaysEditable?: boolean | undefined,
    ): IField => {
        const { status, ticket_options, ticket_type_id, editable } = participant;
        const productMap = getMapByGetter(products, p => p.id);
        return {
            label: this.sharedSvc.translate(TRANSLATION_MAP.TICKET),
            name: this.attributeName.ticket_type_id,
            id: this.attributeName.ticket_type_id,
            editable: editable && (alwaysEditable ?? getFieldEditableByStatus(status)),
            schema: {
                type: ['string', 'null'],
                enum: ticket_options
                    ?.map(({ value }) => value || '')
                    .filter(value => ticket_type_id ? value : true)
                || [],
            },
            attributeTranslations: ticket_options?.map((ticket) => ({
                value: ticket.value,
                trans: {
                    [this.currentLanguage]: productMap[ticket.value ?? '']?.name || ticket.label,
                },
            })),
        } as IField;
    };

    getParticipantAddons = (ticketId: string | null, params: { participantId: string }) => {
        return this.multiRegistrationSvc.getAddonOptionsByParticipantTicket(params.participantId, ticketId) || [];
    };

    createAddonsField = (addon_options: TicketOption[] | undefined): IField | null=> {
        return addon_options?.length ?{
            builtin: false,
            editable: true,
            name: MULTI_REGISTRATION_ATTRIBUTE_NAMES.participant_addons_field_id,
            id: MULTI_REGISTRATION_ATTRIBUTE_NAMES.participant_addons_field_id,
            label: this.sharedSvc.translate(TRANSLATION_MAP.ADDONS),
            schema: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: addon_options.map(item => item.value),
                }
            },
            attributeTranslations: addon_options.map(({label, value}) => ({
                value,
                trans: {
                    [this.currentLanguage]: label
                },
            })),
            component: FieldComponentType.checkboxes,
            type: 'multi-select',
        } as IField : null;
    };

    createInputFieldElement = (
        dummyValue: null,
        field: IField,
        participant: UIParticipant,
        validateFnName = 'azavista.validateSubParticipantField'
    ) => {
        let fieldHtml = this.inputFieldCreatorSvc
            .createInputFieldBlock(field)
            .replace(
                this.inputFieldCreatorSvc['validateFnName'],
                validateFnName
            );
        // we treated all input fields in the multi-registration  to become mandatory except the following field
        if (field.id !== this.attributeName.main_participant_assigned_field_id && field.id !== this.attributeName.participant_addons_field_id) {
            fieldHtml = fieldHtml.replace(
                ' aza-dependency ',
                 ` aza-dependency ${GlobalAttributeName.azaInputRequired} `,
            );
        }
        return this.getSanitizedHtml(fieldHtml);
    };

    getSanitizedHtml = (fieldHtml: string) => {
        return this.sanitizer.bypassSecurityTrustHtml(fieldHtml);
    };

    createAssignMainParticipantFieldData = (
        state: UIState,
        config: MultiRegistrationConfig
    ): { field: IField; data: UIParticipant } => {
        const { main_participant, can_add_participant } = state;
        const isAssigned = !!main_participant.ticket_type_id;
        return {
            field: {
                ...getAssignMainParticipantField({
                    isAssigned,
                    can_add_participant,
                    sharedSvc: this.sharedSvc,
                }),
                editable: !(config.mainparticipant_assigned_required && isAssigned)
            },
            data: {
                ...main_participant,
                participant_attributes: {
                    ...(main_participant.participant_attributes || {}),
                    [this.attributeName.main_participant_assigned_field_id]:
                        isAssigned,
                },
            },
        };
    };

}
